import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from 'src/lib/supabaseClient';
import { useAuth } from 'src/context/AuthContext';

interface AuthenticatedImageProps {
  src: string;
  alt: string;
  className?: string;
  fallback?: React.ReactNode;
  onLoad?: () => void;
  onError?: (error: any) => void;
}

// Cache for signed URLs with expiration tracking
interface CachedUrl {
  signedUrl: string;
  expiresAt: number;
}

const urlCache = new Map<string, CachedUrl>();

// --- Persistent sessionStorage cache helpers ---
const SESSION_STORAGE_KEY = 'supabaseSignedUrlCache';

function saveCacheToSessionStorage() {
  try {
    const obj: Record<string, CachedUrl> = {};
    urlCache.forEach((value, key) => {
      obj[key] = value;
    });
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(obj));
  } catch (e) {
    // Ignore quota errors
  }
}

function loadCacheFromSessionStorage() {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw) as Record<string, CachedUrl>;
    Object.entries(obj).forEach(([key, value]) => {
      // Only restore if not expired
      if ((value as CachedUrl).expiresAt > Date.now()) {
        urlCache.set(key, value as CachedUrl);
      }
    });
  } catch (e) {
    // Ignore parse errors
  }
}

// Load cache on module load
loadCacheFromSessionStorage();

// Clean up expired URLs from cache periodically
const cleanupCache = () => {
  const now = Date.now();
  for (const [key, value] of urlCache.entries()) {
    if (value.expiresAt <= now) {
      urlCache.delete(key);
    }
  }
  saveCacheToSessionStorage(); // Keep sessionStorage in sync
};

// Run cleanup every 10 minutes
setInterval(cleanupCache, 10 * 60 * 1000);

const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({
  src,
  alt,
  className,
  fallback,
  onLoad,
  onError
}) => {
  const { userType, user, session } = useAuth();
  const [signedUrl, setSignedUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [lastLoadedUrl, setLastLoadedUrl] = useState<string>('');

  // Use refs to track the current request and prevent race conditions
  const currentSrcRef = useRef<string>('');
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastSuccessfulUrlRef = useRef<string>('');

  // Memoized function to get signed URL
  const getSignedUrl = useCallback(async (srcUrl: string) => {
    const { isStorageUrl, getSupportedBucketsForUserType } = await import('src/utils/storageUtils');
    
    // Skip if this is the same URL we already successfully loaded
    if (srcUrl === lastSuccessfulUrlRef.current && signedUrl) {
      setLoading(false);
      return;
    }

    // Abort any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    // Update current src ref
    currentSrcRef.current = srcUrl;

    if (!srcUrl) {
      setLoading(false);
      return;
    }

    // If the src is not a storage URL (e.g., external URL or data URL), use it directly
    if (!isStorageUrl(srcUrl)) {
      if (signal.aborted) return;
      setSignedUrl(srcUrl);
      lastSuccessfulUrlRef.current = srcUrl; // Track successful load
      setLoading(false);
      return;
    }

    // Only the avatars and tutor profile pic bucket should have public access - all others are private
    const publicBuckets = ['avatars', 'tutor-uploads-pic'];
    const privateBuckets = ['student-uploads', 'tutor-uploads', 'admin-uploads'];
    const url = new URL(srcUrl);
    const pathParts = url.pathname.split('/');

    // Check if this URL contains a truly public bucket
    const isPublicBucket = publicBuckets.some(bucket =>
      pathParts.includes(bucket)
    );

    if (isPublicBucket) {
      if (signal.aborted) return;
      setSignedUrl(srcUrl);
      lastSuccessfulUrlRef.current = srcUrl; // Track successful load
      setLoading(false);
      return;
    }

    // Check if this is a private bucket that requires authentication
    const isPrivateBucket = privateBuckets.some(bucket =>
      pathParts.includes(bucket)
    );

    if (isPrivateBucket && (!user || !session)) {
      console.log('Private bucket requires authentication, but user not authenticated');
      if (signal.aborted) return;
      setError('Authentication required');
      setLoading(false);
      return;
    }

    // For private buckets with authenticated users, we need signed URLs
    // This ensures proper access control while allowing authenticated users
    // to view profile pictures of other users

    try {
      // Check cache first
      const cached = urlCache.get(srcUrl);
      const now = Date.now();

      // Use cached URL if it exists and hasn't expired
      if (cached && cached.expiresAt > now) {
        console.log('✅ Using cached signed URL for:', srcUrl);
        if (signal.aborted) return;
        setSignedUrl(cached.signedUrl);
        lastSuccessfulUrlRef.current = srcUrl; // Track successful load
        setLoading(false);
        return;
      }

      // Extract the path from the full URL
      // URL format: https://[project].supabase.co/storage/v1/object/public/bucket/path
      // or: https://[project].supabase.co/storage/v1/object/sign/bucket/path
      const url = new URL(src);
      const pathParts = url.pathname.split('/');

      // Find the bucket and path - use user-type specific buckets
      const supportedBuckets = getSupportedBucketsForUserType(userType);
      let bucketIndex = -1;

      for (const bucket of supportedBuckets) {
        bucketIndex = pathParts.findIndex(part => part === bucket);
        if (bucketIndex !== -1) {
          break;
        }
      }

      if (bucketIndex === -1) {
        console.warn(`No supported bucket found in URL for user type ${userType}. URL: ${src}`);
        console.warn(`Supported buckets: ${supportedBuckets.join(', ')}`);
        // Fallback to direct URL usage instead of failing
        console.log('Falling back to direct URL usage');
        setSignedUrl(src);
        lastSuccessfulUrlRef.current = src; // Track successful load
        setLoading(false);
        return;
      }

      const bucket = pathParts[bucketIndex];
      const filePath = pathParts.slice(bucketIndex + 1).join('/');

      console.log('Getting signed URL for:', { bucket, filePath });

      // Get a signed URL that includes authentication
      const { data, error: signError } = await supabase.storage
        .from(bucket)
        .createSignedUrl(filePath, 3600); // 1 hour expiry

      if (signError) {
        console.error('Supabase storage error:', signError);

        // Handle specific error types
        if (signError.message?.includes('not found')) {
          console.log('File not found in storage');
          setError('File not found');
        } else if (signError.message?.includes('unauthorized') || signError.message?.includes('permission')) {
          console.log('Unauthorized access to storage');
          setError('Access denied');
        } else {
          console.log('Storage error:', signError.message);
          setError('Storage error');
        }

        setLoading(false);
        return;
      }

      if (data?.signedUrl) {
        // Cache the signed URL with expiration time
        const expiresAt = now + 3600 * 1000; // 1 hour from now
        urlCache.set(src, {
          signedUrl: data.signedUrl,
          expiresAt
        });
        saveCacheToSessionStorage();

        setSignedUrl(data.signedUrl);
        lastSuccessfulUrlRef.current = src; // Track successful load
        console.log('✅ Signed URL created and cached successfully');
      } else {
        throw new Error('No signed URL returned');
      }
    } catch (err) {
      console.error('❌ Error creating signed URL:', err);
      setError(err instanceof Error ? err.message : 'Failed to load image');
      onError?.(err);
      setLoading(false);
    }
  }, [userType, user?.id, session?.access_token]); // Only depend on stable values

  useEffect(() => {
    getSignedUrl(src);
  }, [src, getSignedUrl]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setLoading(false);
    onLoad?.();
    setLastLoadedUrl(signedUrl);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setError('Failed to load image');
    setLoading(false);
    setImageLoaded(false);
    onError?.(e);
  };

  // Always render the <img> tag, with a transparent placeholder if signedUrl is not ready
  // Overlay the fallback (spinner/initials) absolutely, hide it when imageLoaded is true
  return (
    <div className={`relative ${className || ''}`} style={{ display: 'inline-block' }}>
      <img
        src={signedUrl || 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs='} // 1x1 transparent gif
        alt={alt}
        className={className}
        style={{ display: 'block', width: '100%', height: '100%' }}
        onLoad={handleImageLoad}
        onError={handleImageError}
        draggable={false}
      />
      {(!imageLoaded || error) && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-500"
          style={{ zIndex: 1 }}
        >
          {fallback ? fallback : (
            <span className="text-sm">
              {error === 'Authentication required' ? 'Login required' :
                error === 'Access denied' ? 'Access denied' :
                error === 'File not found' ? 'Image not found' :
                error ? 'Failed to load' : ''}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default AuthenticatedImage;

export function invalidateSignedUrlCache(url: string) {
  urlCache.delete(url);
  saveCacheToSessionStorage();
}
